export type MessageChannel = "email" | "sms";

/**
 * One row of the unified feed. Email and SMS come back in the same shape — the server
 * normalises them — so the table renders both without branching. SMS has no subject, so
 * its `preview` carries the whole body.
 */
export type Message = {
  id: string;
  channel: MessageChannel;
  provider_message_id: string;
  recipient: string;
  subject: string;
  preview: string;
  source: "transactional" | "automation" | "campaign";
  source_label: string;
  status: string;
  guest_id: string | null;
  guest_name: string;
  order_id: string | null;
  automation_run_id: string | null;
  campaign_email_id: string | null;
  open_count: number;
  click_count: number;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  failed_at: string | null;
  error_code: string;
  created_at: string;
};

export type MessageEvent = {
  event_type: string;
  occurred_at: string;
  url?: string;
  click_location?: string;
  client?: string;
  os?: string;
  platform?: string;
  geo?: Record<string, any>;
  bounce_type?: string;
  bounce_description?: string;
  is_inactive?: boolean;
};

export type MessageDetail = Message & {
  events: MessageEvent[];
  message_stream?: string;
  tag?: string;
  error_message?: string;
  num_segments?: number | null;
  price?: string;
  price_unit?: string;
};

export type MessagePage = {
  data: Message[];
  /** Opaque — pass back verbatim for the next page. Null means the end of the feed. */
  next_cursor: string | null;
};

export type CommunicationsStats = {
  window_days: number;
  email_sent: number;
  email_delivered: number;
  email_opened: number;
  email_problem: number;
  sms_sent: number;
  sms_delivered: number;
  sms_problem: number;
};

export type MessageFilters = {
  channel?: string;
  status?: string;
  source?: string;
  search?: string;
  guest_id?: string;
  cursor?: string | null;
  limit?: number;
};

class CommunicationsService {
  baseUrl = `${import.meta.env.VITE_API_BASE_URL}`;

  getMessagesAsync = async (filters: MessageFilters = {}): Promise<MessagePage> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    const res = await fetch(`${this.baseUrl}communications/?${params.toString()}`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getMessageAsync = async (
    channel: MessageChannel,
    id: string,
  ): Promise<MessageDetail> => {
    const res = await fetch(`${this.baseUrl}communications/${channel}/${id}/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getStatsAsync = async (days = 30): Promise<CommunicationsStats> => {
    const res = await fetch(`${this.baseUrl}communications/stats/?days=${days}`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  /**
   * The rendered HTML, fetched from Postmark on demand rather than stored.
   *
   * Returns null when Postmark no longer has it — content is retained for 45 days —
   * which is expected rather than an error, so callers fall back to the preview.
   */
  getEmailBodyAsync = async (id: string): Promise<string | null> => {
    const res = await fetch(`${this.baseUrl}communications/email/${id}/body/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.body ?? null;
  };
}

export default new CommunicationsService();
